import { useEffect, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { LayoutDashboard, Inbox, Zap, Settings, LogOut, BarChart3, Bell, BellOff, Volume2, VolumeX, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "@/components/ui/sheet";
import { InstallApp } from "@/components/layout/InstallApp";
import { UpdateApp } from "@/components/layout/UpdateApp";
import { useQuery } from "@tanstack/react-query";
import { useAuth, useBranding } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { api } from "@/lib/api";
import { initials } from "@/lib/format";
import { useNotifications } from "@/hooks/useNotifications";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, id: "dashboard", end: true },
  { to: "/inbox", label: "Casella condivisa", icon: Inbox, id: "inbox" },
  { to: "/report", label: "Report agenti", icon: BarChart3, id: "report", admin: true },
  { to: "/automazione", label: "Automazione", icon: Zap, id: "automation", admin: true },
  { to: "/impostazioni", label: "Impostazioni", icon: Settings, id: "settings", admin: true },
];

const NotifToggles = ({ notif }) => {
  const { desktop, sound, toggleDesktop, toggleSound } = notif;
  const cls = (on) => `flex-1 flex items-center justify-center gap-1.5 text-[11px] font-semibold py-2 rounded-md transition-colors ${on ? "bg-[#1E293B] text-[#25D366]" : "text-slate-500 hover:bg-[#1E293B]/60 hover:text-slate-300"}`;
  return (
    <div className="flex gap-1 px-1 pb-2">
      <button data-testid="notif-desktop-toggle" onClick={toggleDesktop} className={cls(desktop)} title="Notifiche desktop">
        {desktop ? <Bell size={13} /> : <BellOff size={13} />} Desktop
      </button>
      <button data-testid="notif-sound-toggle" onClick={toggleSound} className={cls(sound)} title="Suono notifiche">
        {sound ? <Volume2 size={13} /> : <VolumeX size={13} />} Suono
      </button>
    </div>
  );
};

const UnassignedBadge = () => {
  const { data } = useQuery({
    queryKey: ["conversations", "unassigned-count"],
    queryFn: () => api.get("/conversations", { params: { filter: "unassigned" } }).then((r) => r.data.counts.unassigned),
    refetchInterval: 8000,
  });
  if (!data) return null;
  return <span data-testid="nav-unassigned-badge" className="ml-auto text-[11px] font-bold bg-[#25D366] text-slate-900 rounded-full px-2 py-0.5">{data}</span>;
};

const SidebarBody = ({ notif }) => {
  const { user, logout } = useAuth();
  const { data: brand } = useBranding();
  return (
    <>
        <div className="flex items-center gap-3 px-5 h-16 shrink-0 border-b border-[#1E293B]">
          <BrandLogo size={34} />
          <div className="min-w-0">
            <div data-testid="sidebar-company-name" className="text-white font-bold text-sm truncate">{brand?.company_name || "…"}</div>
            <div className="text-[11px] text-slate-400">WhatsApp Business CRM</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto px-3 py-5 space-y-1">
          <div className="px-3 pb-2 text-[10px] uppercase tracking-[0.18em] text-slate-500 font-semibold">Area di lavoro</div>
          {NAV.filter((n) => !n.admin || user?.role === "admin").map(({ to, label, icon: Icon, id, end }) => (
            <NavLink
              key={to}
              to={to}
              end={end}
              data-testid={`nav-item-${id}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium border-l-2 transition-colors duration-150 ${
                  isActive ? "bg-[#1E293B] text-white border-[#25D366]" : "border-transparent hover:bg-[#1E293B]/60 hover:text-white"
                }`
              }
            >
              <Icon size={18} />
              <span>{label}</span>
              {id === "inbox" && <UnassignedBadge />}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-[#1E293B]">
          <InstallApp />
          <UpdateApp />
          <NotifToggles notif={notif} />
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="relative">
              <div className="w-9 h-9 rounded-full grid place-items-center text-xs font-bold text-white" style={{ background: user?.color }}>
                {initials(user?.name)}
              </div>
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full bg-[#25D366] border-2 border-[#0F172A]" />
            </div>
            <div className="min-w-0 flex-1">
              <div data-testid="current-user-name" className="text-sm text-white font-semibold truncate">{user?.name}</div>
              <div data-testid="current-user-role" className="text-[11px] text-slate-400">{user?.role === "admin" ? "Amministratore" : "Agente"}</div>
            </div>
            <button data-testid="logout-button" onClick={logout} title="Esci" className="p-2 rounded-md hover:bg-[#1E293B] hover:text-white transition-colors">
              <LogOut size={16} />
            </button>
          </div>
        </div>
    </>
  );
};

export default function AppShell() {
  const notif = useNotifications();
  const [open, setOpen] = useState(false);
  const { data: brand } = useBranding();
  const location = useLocation();
  useEffect(() => { setOpen(false); }, [location.pathname]);
  return (
    <div className="h-[100dvh] flex flex-col md:flex-row overflow-hidden">
      <aside className="hidden md:flex w-60 shrink-0 bg-[#0F172A] text-slate-300 flex-col" data-testid="sidebar">
        <SidebarBody notif={notif} />
      </aside>
      <header className="md:hidden shrink-0 bg-[#0F172A] text-white pt-safe" data-testid="mobile-topbar">
        <div className="h-14 px-3 flex items-center gap-3">
          <button data-testid="mobile-menu-button" onClick={() => setOpen(true)} className="w-10 h-10 grid place-items-center rounded-lg hover:bg-[#1E293B]" aria-label="Menu">
            <Menu size={20} />
          </button>
          <BrandLogo size={28} />
          <span className="font-bold text-sm truncate">{brand?.company_name}</span>
          <span className="ml-auto"><UnassignedBadge /></span>
        </div>
      </header>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="w-72 p-0 bg-[#0F172A] text-slate-300 border-[#1E293B] flex flex-col" data-testid="mobile-sidebar">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <SidebarBody notif={notif} />
        </SheetContent>
      </Sheet>
      <main className="flex-1 min-h-0 min-w-0 overflow-hidden bg-[#F8FAFC]">
        <Outlet />
      </main>
    </div>
  );
}
